import moment from 'moment-timezone'
import { fromZonedTime, toZonedTime, format } from 'date-fns-tz'
import differenceInMinutes from 'date-fns/differenceInMinutes'
import startOfMonth from 'date-fns/startOfMonth'
import endOfMonth from 'date-fns/endOfMonth'
import subDays from 'date-fns/subDays'
import subYears from 'date-fns/subYears'
import startOfDay from 'date-fns/startOfDay'
import addDays from 'date-fns/addDays'
import isBefore from 'date-fns/isBefore'
import Cookies from 'js-cookie'

import Icon from '../components/Icons'

import {
  APP_COLORS,
  TIMEZONES,
  PASSWORD_SYMBOLS_AT_LEAST,
  ONE_DAY_DURATION,
  SECOND,
  ONE_HOUR_DURATION,
  MINUTE,
  ONE_HOUR,
} from './constants'

export const getLastPartOfUrl = (url) => url.substr(url.lastIndexOf('/') + 1)

export const getTrainingType = (training) => {
  try {
    if (training.reservationType) return training.reservationType
    if (training.typeOfEvent === 'amenity') return 'amenity'
    if (training.event) return 'event'
    if (training.type.name === 'Reminder') return 'reminder'
    if (training.mainClient) return 'appointment'
  } catch {
    return 'class'
  }
  return 'class'
}

export const sortArrayOfObjects = (array, property, desc = false) => {
  array.sort((_a, _b) => {
    let a = _a[property]
    let b = _b[property]
    // force null and undefined to the bottom
    a = a === null || a === undefined ? -Infinity : a
    b = b === null || b === undefined ? -Infinity : b
    // force any string values to lowercase
    a = a === 'string' ? a.toLowerCase() : a
    b = b === 'string' ? b.toLowerCase() : b
    // Return either 1 or -1 to indicate a sort priority
    if (a > b) {
      return 1
    }
    if (a < b) {
      return -1
    }
    // returning 0, undefined or any falsey value will use subsequent sorts or the index as a tiebreaker
    return 0
  })

  return desc ? array.reverse() : array
}

export const digitalSorter = (a, b) => {
  return a === b ? 0 : a < b ? 1 : -1
}

export const alphabeticalSorter = (a, b) => {
  if (a.toLowerCase() < b.toLowerCase()) return -1
  if (a.toLowerCase() > b.toLowerCase()) return 1
  return 0
}

export const camelCaseToSpaces = (str) => {
  return str.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase())
}

export const downloadFile = (blob, fileName) => {
  const a = document.createElement('a')
  a.style = 'display: none'
  document.body.appendChild(a)
  const url = window.URL.createObjectURL(blob)
  a.href = url
  a.download = `${fileName}`
  a.click()
  window.URL.revokeObjectURL(url)
}

export const getTimezoneByRole = (userData) => {
  const {
    buildingId,
    user: { role },
  } = userData

  switch (role) {
    case 'Administrator':
      return TIMEZONES.CT
    case 'Trainer':
      return null
    case 'Manager':
      return TIMEZONES.CT
    default:
      return buildingId.timezone
  }
}

export const getBuildingTimezoneFromEvent = (event) => {
  if (typeof event.buildingId === 'number' && event.buildingInfo) {
    return event.buildingInfo.timezone
  } else if (!event.buildingInfo && typeof event.buildingId === 'object') {
    return event.buildingId.timezone
  } else if (event.buildingInfo && typeof event.buildingId === 'object') {
    return event.buildingInfo.timezone
  } else {
    // Fallback to avoid crashes
    return 'America/Chicago'
  }
}

export const getTimezonedDate = (timestamp, timezone) => {
  const zoned = moment(timestamp).tz(timezone)
  return new Date(new Date(zoned).setHours(0, 0, 0, 0))
}

export const getRangeInBuildingTz = (startDate, endDate, buildingTimezone) => ({
  start: Number.isInteger(startDate)
    ? startDate
    : fromZonedTime(startDate || startOfMonth(new Date()), buildingTimezone).getTime(),
  end: Number.isInteger(endDate)
    ? endDate
    : fromZonedTime(endDate || endOfMonth(new Date()), buildingTimezone).getTime(),
})

// Multi-day reservation segments (getMultidayAmenities) all carry check-in/check-out as
// startTime/endTime - but check-in only applies to the first day and check-out to the last;
// days in between are fully taken. Leaves startTime/endTime on the event untouched, since the
// amenity popup reads them as the check-in/check-out times.
const getMultidayDayMinutes = (training, timezone) => {
  const day = moment.tz(training.date, timezone)
  const isFirstDay = day.isSame(moment.tz(training.startDate, timezone), 'day')
  const isLastDay = day.isSame(moment.tz(training.endDate, timezone), 'day')

  return {
    endTime: isLastDay ? training.endTime : 24 * 60,
    startTime: isFirstDay ? training.startTime : 0,
  }
}

export const parseEventsForCalendar = (events, managementCalendar = false, dayView = false) => {
  return events.map((training) => {
    const { date, duration, name } = training
    let title = name
    if (managementCalendar && getTrainingType(training) === 'appointment') {
      try {
        const client = training.clients[0].user
        title = client.firstName + ' ' + client.lastName + ' - ' + training.type.name
      } catch {
        title = name
      }
    }

    const buildingTimezone = getBuildingTimezoneFromEvent(training)
    const timezoneDate = toZonedTime(new Date(parseInt(date)), buildingTimezone)

    let start = new Date(format(timezoneDate, "yyyy-MM-dd'T'HH:mm:ss"))

    // Reservations and time blocks keep the day in `date` and the hours in startTime/endTime
    // (minutes after midnight), unlike events, where `date` is the exact start.
    const isTimeOfDayBased =
      training.typeOfEvent === 'amenity' ||
      ['reservation', 'timeBlock'].includes(training.reservationType)
    // Multi-day reservations carry check-in/check-out as startTime/endTime (check-out is often
    // earlier than check-in), so they only use them in day/agenda view, as before.
    const useTimeOfDay = isTimeOfDayBased && (dayView || !training.isMultiday)

    if (useTimeOfDay) {
      const { startTime, endTime } = training.isMultiday
        ? getMultidayDayMinutes(training, buildingTimezone)
        : training
      const dayStart = new Date(start).setHours(0, 0, 0, 0)
      const endMinutes = endTime ?? 24 * 60 // null endTime = until end of day

      return {
        ...training,
        start: new Date(dayStart + (startTime ?? 0) * MINUTE),
        // An end of 1440 (midnight) would put `end` on the next day, which sends the item to
        // the hidden all-day row in week/day view - end 1 ms before midnight instead
        end: new Date(dayStart + endMinutes * MINUTE - (endMinutes >= 24 * 60 ? 1 : 0)),
        title,
      }
    }

    return {
      ...training,
      start,
      end: duration ? new Date(start.getTime() + duration * 60 * 1000) : start,
      title,
    }
  })
}

export const lightenColor = (color, percent) => {
  let num, amt, R, B, G
  num = parseInt(color.substring(1), 16)
  amt = Math.round(2.55 * percent)
  R = (num >> 16) + amt
  B = ((num >> 8) & 0x00ff) + amt
  G = (num & 0x0000ff) + amt

  return (
    0x1000000 +
    (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
    (B < 255 ? (B < 1 ? 0 : B) : 255) * 0x100 +
    (G < 255 ? (G < 1 ? 0 : G) : 255)
  )
    .toString(16)
    .slice(1)
}

export const parseUrl = (name) => {
  return name.toLowerCase().replace(/ /g, '-')
}

export const getFullDateFromDayMinutes = (dayMinutes) => {
  const hours = Math.floor(dayMinutes / 60)
  const minutes = dayMinutes % 60

  const date = moment(`${hours}:${minutes}`, 'HH:mm')
  date.toDate()
  return date
}

export const getTimeFromDayMinutes = (dayMinutes) => {
  const hours = Math.floor(dayMinutes / 60)
  const minutes = dayMinutes % 60

  const formattedTime = moment(`${hours}:${minutes}`, 'HH:mm').format('hh:mma')
  return formattedTime
}

export const convertMinutesToDaysHoursMinutes = (minutes) => {
  const days = Math.floor(minutes / (60 * 24))
  const remainingHours = Math.floor((minutes % (60 * 24)) / 60)
  const remainingMinutes = minutes % 60

  const daysString = days > 0 ? days + 'd' : ''
  const hoursString = remainingHours > 0 ? remainingHours + 'h' : ''
  const minutesString = remainingMinutes > 0 ? remainingMinutes + 'm' : ''

  return daysString + ' ' + hoursString + ' ' + minutesString
}

export const getMinutesBetweenDates = (startTime, endTime) =>
  differenceInMinutes(endTime, startTime, { roundingMethod: 'round' })

export const getTimeIntervals = ({ startTime, endTime, minInterval, currentInterval }) => {
  const slots = []

  if (!startTime || !endTime) {
    const slotsNumber = currentInterval / minInterval

    for (let i = 0; i < slotsNumber; i++) {
      slots.push({ label: minInterval * (i + 1) + ' min', value: minInterval * (i + 1) })
    }

    return slots
  }

  const minutes = getMinutesBetweenDates(startTime, endTime)
  const slotsNumber = minutes / minInterval

  for (let i = 0; i < slotsNumber; i++) {
    slots.push({ label: minInterval * (i + 1) + ' min', value: minInterval * (i + 1) })
  }

  return slots
}

export const getMinutesofDayFromDate = (date) => {
  const startDay = moment(date).startOf('day')
  const duration = moment.duration(moment(date).diff(startDay))
  return Math.floor(duration.asMinutes())
}

export const getFormatedFullDate = (timestamp, timezone) => {
  return moment(timestamp)
    .tz(timezone || 'America/Chicago')
    .format('MM/DD/YY hh:mm A')
}

export const getFormatedDate = (timestamp, timezone) => {
  const date = moment(timestamp).tz(timezone || 'America/Chicago')
  const month = date.format('MMM')
  const day = date.format('Do')
  const year = date.format('YYYY')
  const formattedDate = `${month} ${day} ${year}`
  return formattedDate
}

export const dayMinutesToHoursMinutes = (dayMinutes) => {
  const hours = Math.floor(dayMinutes / 60)
  let minutes = dayMinutes % 60
  minutes = minutes < 10 ? `0${minutes}` : minutes

  const time = `${hours > 12 ? hours - 12 : hours}:${minutes}${hours > 11 ? 'pm' : 'am'}`

  return time === '0:00am' || dayMinutes === 1440
    ? '12:00am'
    : `${hours > 12 ? hours - 12 : hours}:${minutes}${hours > 11 ? 'pm' : 'am'}`
}

export const getIncludeTimes = (startDate, interval) => {
  if (!startDate) {
    return undefined
  }

  const includeTimes = []
  const dayMinutes = getMinutesofDayFromDate(startDate)

  for (let i = dayMinutes + interval; i < 1440; i += interval) {
    includeTimes.push(moment().startOf('day').add(i, 'minutes').toDate())
  }
  return includeTimes
}

export const inject1159IfPossible = (startDate, interval) => {
  if (!startDate) return []

  const includeTimes = []
  const dayMinutes = getMinutesofDayFromDate(startDate)
  for (let i = dayMinutes + interval; i <= 1440; i += interval) {
    if (i === 1440) {
      includeTimes.push(moment().endOf('day').subtract(1, 'second').toDate())
    }
  }
  return includeTimes
}

export const hasArrayEntries = (collection) =>
  collection && Array.isArray(collection) && collection.length !== 0

export const getDayNameByNumber = (id) => {
  const dict = [
    { value: 0, text: 'Sunday' },
    { value: 1, text: 'Monday' },
    { value: 2, text: 'Tuesday' },
    { value: 3, text: 'Wednesday' },
    { value: 4, text: 'Thursday' },
    { value: 5, text: 'Friday' },
    { value: 6, text: 'Saturday' },
  ]
  return dict.find((day) => day.value === id).text
}

export const getDayNumberByName = (name) => {
  const dict = [
    { value: 0, text: 'Sunday' },
    { value: 1, text: 'Monday' },
    { value: 2, text: 'Tuesday' },
    { value: 3, text: 'Wednesday' },
    { value: 4, text: 'Thursday' },
    { value: 5, text: 'Friday' },
    { value: 6, text: 'Saturday' },
  ]
  return dict.find((day) => day.text === name).value
}

export const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1)

// const customRender = (ui) => render(<Provider store={createStore(rootReducer)}>{ui}</Provider>)

// export { customRender as render }

export const getTimezoneShortName = (tzDbName) => {
  switch (tzDbName) {
    case TIMEZONES.CT:
      return 'CT'
    case TIMEZONES.ET:
      return 'ET'
    case TIMEZONES.MT:
      return 'MT'
    case TIMEZONES.AZ:
      return 'MT'
    case TIMEZONES.PT:
      return 'PT'
    default:
      return 'Unknown timezone'
  }
}

export const getTimezoneOffset = (timezone) => {
  switch (timezone) {
    case TIMEZONES.CT:
      return -5
    case TIMEZONES.ET:
      return -4
    case TIMEZONES.MT:
      return -6
    case TIMEZONES.AZ:
      return -7
    case TIMEZONES.PT:
      return -7
    default:
      return -4
  }
}

export const getTimezoneStamp = (targetUnixTime, building) => {
  const dateObj = new Date(targetUnixTime)
  const year = moment(dateObj).format('YYYY')
  const month = moment(dateObj).format('MM')
  const day = moment(dateObj).format('DD')

  const momentObj = moment(`${year}-${month}-${day}T09:00:00.000Z`)
    .tz((building[0] ? building[0].timezone : building.timezone) || 'America/Chicago')
    .set('hour', 0)
    .format()

  return new Date(momentObj).getTime()
}

export const getProperRole = (role) => {
  switch (role) {
    case 'Client':
      return 'Resident'
    case 'PropertyManager':
      return 'Property Manager'
    case 'ShiftManager':
      return 'Front Desk'
    case 'Staff':
      return 'Maintenance'
    case 'Administrator':
      return 'Administrator'
    case 'Manager':
      return 'Manager'
    case 'Valet':
      return 'Valet'
    case 'ParkingStaff':
      return 'Parking Staff'
    default:
      return ''
  }
}

export const getProperRoleForChat = (role) => {
  switch (role) {
    case 'Client':
      return 'Resident'
    case 'PropertyManager':
      return 'Management Team'
    case 'ShiftManager':
      return 'Front Desk'
    case 'Staff':
      return 'Maintenance'
    case 'Administrator':
      return 'Administrator'
    case 'Manager':
      return 'Manager'
    default:
      return ''
  }
}

export const getPasswordInfo = (password) => {
  return {
    symbolsAtLeast: password.length >= PASSWORD_SYMBOLS_AT_LEAST,
    upperCase: /[A-Z]/.test(password),
    lowerCase: /[a-z]/.test(password),
    oneNumber: /\d/.test(password),
  }
}

export const renderPasswordCheckers = (passwordCheckers) => {
  return passwordCheckers.map((checker, i) => (
    <div key={checker.name + i} className="password__check">
      <div className="check__circle">
        {checker.value && (
          <Icon
            icon={'checkmark'}
            className="check__icon"
            viewBox="0 0 30 30"
            svgProps={{ strokeWidth: 3 }}
          />
        )}
      </div>
      <p data-testid={checker.name} className={!checker.value ? '' : 'check__right'}>
        {checker.name}
      </p>
    </div>
  ))
}

export const filterPhantomBookings = (blockedTimeslot, unixStartDate, unixEndDate, timezone) => {
  let blockedCalendarTimeslots = []
  blockedTimeslot.phantomBookings.map((phantomBooking) => {
    if (
      (phantomBooking.date <= unixStartDate && phantomBooking.endDate >= unixStartDate) ||
      (phantomBooking.date <= unixEndDate && phantomBooking.endDate >= unixEndDate) ||
      (phantomBooking.date >= unixStartDate && phantomBooking.endDate <= unixEndDate)
    ) {
      for (let day = phantomBooking.date; day <= phantomBooking.endDate; day += ONE_DAY_DURATION) {
        blockedCalendarTimeslots = [
          ...blockedCalendarTimeslots,
          {
            date: fromZonedTime(
              moment(day).utcOffset(0, false).format('YYYY-MM-DDTHH:mm:ss'),
              timezone,
            ).getTime(),
            isMultidayBooking: blockedTimeslot.isMultidayBooking,
            name: blockedTimeslot.name,
            duration: 0,
            reservationType: 'phantomBooking',
            locationId: phantomBooking.locationId,
          },
        ]
      }
    }
  })
  return blockedCalendarTimeslots
}

export const filterBlockedDays = (blockedTimeslot, unixStartDate, unixEndDate, timezone) => {
  let blockedCalendarDays = []
  blockedTimeslot.blockedDays.weekly.forEach((blockedDay) => {
    const dayOfWeek = moment(unixStartDate).clone().day(blockedDay.date)
    while (dayOfWeek.isBefore(moment(unixEndDate))) {
      blockedCalendarDays = [
        ...blockedCalendarDays,
        {
          date: dayOfWeek.unix() * SECOND,
          isWeeklyBlockedDay: true,
          name: blockedTimeslot.name,
          buildingId: { timezone },
          duration: 0,
          reservationType: 'blockedDay',
        },
      ]
      dayOfWeek.add(7, 'days')
    }
  })
  blockedTimeslot.blockedDays.regular.forEach((blockedDay) => {
    blockedCalendarDays = [
      ...blockedCalendarDays,
      {
        date: blockedDay.date,
        name: blockedTimeslot.name,
        buildingId: { timezone },
        duration: 0,
        reservationType: 'blockedDay',
      },
    ]
  })
  return blockedCalendarDays
}

export const filterUnavailableSlots = (blockedTimeslot, unixStartDate, unixEndDate, timezone) => {
  let unavailableSlotDays = []
  blockedTimeslot.unavailableSlots.weekly.forEach((unavailableSlot) => {
    const timezonedValue = fromZonedTime(
      moment(
        isNaN(unixStartDate)
          ? unixStartDate.setHours(0, 0, 0, 0)
          : new Date(unixStartDate).setHours(0, 0, 0, 0),
      ).format('YYYY-MM-DDTHH:mm:ss'),
      timezone,
    ).getTime()
    const dayOfWeek = moment(timezonedValue).tz(timezone).clone().day(unavailableSlot.date)
    let weeklyUnavailableSlots = []
    while (dayOfWeek.isBefore(moment(unixEndDate))) {
      weeklyUnavailableSlots = [
        ...weeklyUnavailableSlots,
        {
          date: dayOfWeek.unix() * SECOND,
          startTime: unavailableSlot.startTime,
          endTime: unavailableSlot.endTime,
          isMultidayBooking: !blockedTimeslot.isMultidayBooking,
          name: blockedTimeslot.name,
          buildingId: { timezone },
          duration: 0,
          reservationType: 'unavailableSlot',
        },
      ]
      dayOfWeek.add(7, 'days')
    }
    unavailableSlotDays = [
      ...unavailableSlotDays,
      ...weeklyUnavailableSlots.map((slot) => ({
        ...slot,
        isWeeklySlot: !!weeklyUnavailableSlots.length,
      })),
    ]
  })
  blockedTimeslot.unavailableSlots.regular.forEach((unavailableSlot) => {
    const lastDay =
      unavailableSlot.endDate && unavailableSlot.endDate > unavailableSlot.date
        ? unavailableSlot.endDate
        : unavailableSlot.date

    for (let day = unavailableSlot.date; day <= lastDay; day += ONE_DAY_DURATION) {
      unavailableSlotDays = [
        ...unavailableSlotDays,
        {
          date: day,
          startTime: unavailableSlot.startTime,
          endTime: unavailableSlot.endTime,
          isMultidayBooking: !blockedTimeslot.isMultidayBooking,
          name: blockedTimeslot.name,
          buildingId: { timezone },
          duration: 0,
          reservationType: 'unavailableSlot',
        },
      ]
    }
  })
  return unavailableSlotDays
}

export const convertCalendarDateToBuildingTimestamp = (value, userData, activeBuilding) => {
  const dateObj = new Date(value)
  const year = moment(dateObj).format('YYYY')
  const month = moment(dateObj).format('MM')
  const day = moment(dateObj).format('DD')
  const building = userData.user.role === 'PropertyManager' ? activeBuilding : userData.buildingId
  const momentObj = moment(`${year}-${month}-${day}T09:00:00.000Z`)
    .tz(building ? (building[0] ? building[0].timezone : building.timezone) : 'America/Chicago')
    .set('hour', 0)
    .format()

  return new Date(momentObj).getTime()
}

const getMultidayAmenities = (multidayAmenity, buildingData) => {
  const multidayAmenities = []
  const timezone = buildingData?.timezone ?? 'America/Chicago'
  // Step by calendar day in the building timezone, not by 24h - across a DST change a 24h step
  // lands on the same day twice and skips the next one
  const lastDay = moment.tz(multidayAmenity.endDate, timezone).startOf('day')
  for (
    let day = moment.tz(multidayAmenity.date, timezone).startOf('day');
    !day.isAfter(lastDay);
    day.add(1, 'day')
  ) {
    multidayAmenities.push({
      ...multidayAmenity,
      startDate: multidayAmenity.date,
      date: day.valueOf(),
      endDate: multidayAmenity.endDate,
      name: multidayAmenity.amenity.name,
      capacity: (multidayAmenity.endDate - multidayAmenity.date) / ONE_DAY_DURATION,
      duration: ONE_HOUR_DURATION,
      typeOfEvent: 'amenity',
      reservationType: 'reservation',
      buildingId: buildingData,
      isMultiday: true,
      startTime: multidayAmenity.amenity.checkIn,
      endTime: multidayAmenity.amenity.checkOut,
    })
  }
  return multidayAmenities
}

export const parseToReservationsArray = (amenities, multidayAmenities, buildingData) => {
  let reservations = []
  multidayAmenities
    .filter((multidayAmenity) => multidayAmenity && multidayAmenity.date)
    .forEach((multidayAmenity) => {
      reservations = [...reservations, ...getMultidayAmenities(multidayAmenity, buildingData)]
    })
  amenities.forEach((amenity) => {
    const reservationsWithData = amenity.reservations.map((reservation) => ({
      ...reservation,
      typeOfEvent: 'amenity',
      reservationType: 'reservation',
      capacity: amenity.amenity.capacity,
      name: amenity.amenity.name,
      buildingId: buildingData,
      duration: reservation.endTime - reservation.startTime,
    }))
    reservations = [...reservations, ...reservationsWithData]
  })
  return reservations.filter((reservations) => !reservations.cancelled)
}

export const parseToBlockedTimeslots = (amenity) => {
  return {
    type: 'blockedTimeslot',
    isMultidayBooking: amenity.multiDayBooking,
    name: amenity.name,
    phantomBookings:
      amenity.managerialBooking &&
      amenity.managerialBooking.map((booking) => {
        return { date: booking.startDate, endDate: booking.endDate }
      }),
    [amenity.multiDayBooking ? 'blockedDays' : 'unavailableSlots']: amenity.unavailableSlots,
  }
}

// A multi-day time block covers [date + startTime, endDate + endTime] - split it into one
// calendar item per day (like getMultidayAmenities does for multi-day reservations), since
// week/day view hides events that cross midnight.
const splitTimeBlockIntoDays = (block, timezone) => {
  if (!block.endDate || block.endDate <= block.date) return [block]

  const days = []
  const lastDay = moment.tz(block.endDate, timezone).startOf('day')
  for (
    let day = moment.tz(block.date, timezone).startOf('day');
    !day.isAfter(lastDay);
    day.add(1, 'day')
  ) {
    days.push({
      ...block,
      startDate: block.date, // `date` becomes the day; keep the block's own start for its range
      date: day.valueOf(),
      startTime: days.length === 0 ? block.startTime : null,
      endTime: day.isSame(lastDay, 'day') ? block.endTime : null,
    })
  }
  return days
}

export const mapTimeBlockToCalendarEvent = (blocks = [], buildingData) =>
  blocks
    .flatMap((block) => splitTimeBlockIntoDays(block, buildingData?.timezone ?? 'America/Chicago'))
    .map((block) => ({
      ...block,
      timeBlockId: block.id,
      reservationType: 'timeBlock',
      reason: block.reason ?? 'General', // null for blocks created before reasons existed
      name: block.amenityId.name,
      amenityName: block.amenityId.name,
      locationId: block.locationId?.id ?? null,
      locationName: block.locationId?.name ?? null,
      buildingId: buildingData, // object; needed for the filter and the timezone
      duration: (block.endTime ?? 0) - (block.startTime ?? 0),
      // don't set `type` (it would flip isRegularEvent) or `amenity` (AgendaEvent's reservation row)
    }))

export const mapClientToUser = ({ id, apartment: unit, user, phone, creditCardId }) => ({
  value: id,
  userId: user.id,
  label: `${user.firstName} ${user.lastName}`,
  email: user.email,
  phone,
  unit,
  creditCardId,
})

export const getBlockedDay = (date, blockedDays) => {
  return (
    blockedDays.length &&
    blockedDays.find((blockedDay) => {
      const dateToCompare = blockedDay.isWeeklyBlockedDay
        ? moment(blockedDay.date).format('DD-MM-YYYY')
        : moment(blockedDay.date).tz(blockedDay.buildingId.timezone).format('DD-MM-YYYY')
      return dateToCompare === moment(date).format('DD-MM-YYYY')
    })
  )
}

export const formatStartAndEndDates = (trainingType, event, timezone) => {
  let start
  let end
  if (
    ['amenity', 'reservation', 'unavailableSlot', 'phantomBooking', 'timeBlock'].includes(
      trainingType,
    )
  ) {
    const startMoment = moment(event.endDate || event.date + event.startTime * MINUTE).tz(
      timezone || 'America/Chicago',
    )
    if (startMoment.isDST()) {
      startMoment.utcOffset(getTimezoneOffset(timezone || 'America/Chicago'))
    }
    start = startMoment.format('hh:mmA')

    const endMoment = moment(event.date + event.endTime * MINUTE).tz(timezone || 'America/Chicago')
    if (endMoment.isDST()) {
      endMoment.utcOffset(getTimezoneOffset(timezone || 'America/Chicago'))
    }
    end = endMoment.format('hh:mmA')
  } else {
    const startMoment = moment(event.date).tz(timezone)
    if (startMoment.isDST()) {
      startMoment.utcOffset(getTimezoneOffset(timezone))
    }
    start = startMoment.format('hh:mmA')

    const endMoment = moment(event.date).add(event.duration, 'm').tz(timezone)

    if (endMoment.isDST()) {
      endMoment.utcOffset(getTimezoneOffset(timezone))
    }
    end = endMoment.format('hh:mmA')
  }
  return [start, end]
}

export const getBlockedSlots = (
  activeBuilding,
  userData,
  filters,
  blockedTimeslots,
  startDate,
  endDate,
) => {
  const eventType = filters.calendar.eventType

  const building = userData.user.role === 'PropertyManager' ? activeBuilding : userData.buildingId

  const timezone = building ? building.timezone : 'America/Chicago'

  const unixStartDate = isNaN(startDate) ? startDate : moment(startDate).valueOf()
  const unixEndDate = isNaN(endDate) ? endDate : moment(endDate).valueOf()

  let blockedCalendarTimeslots = []
  let blockedCalendarDays = []
  let unavailableSlotDays = []
  if (eventType === 'reservation') {
    const chooseReservationFilter = filters.calendar['Choose Reservation']
    const selectSpaceFilter = filters.calendar['Select Space']
    const blockedTimeslotsFilteredByAmenity =
      chooseReservationFilter.length > 0
        ? blockedTimeslots.filter((blockedTimeslot) => {
            const sel = chooseReservationFilter.map((item) => item.text)
            return blockedTimeslot && sel.includes(blockedTimeslot.name)
          })
        : []

    blockedTimeslotsFilteredByAmenity.map((blockedTimeslot) => {
      if (blockedTimeslot.isMultidayBooking) {
        const phantomBookings = filterPhantomBookings(
          blockedTimeslot,
          unixStartDate,
          unixEndDate,
          timezone,
        )
        const filteredPhantomBookings =
          selectSpaceFilter.length > 0
            ? phantomBookings.filter((booking) =>
                selectSpaceFilter.some((loc) => loc.id === booking.locationId),
              )
            : phantomBookings

        blockedCalendarTimeslots = [...blockedCalendarTimeslots, ...filteredPhantomBookings]
        blockedCalendarDays = [
          ...blockedCalendarDays,
          ...filterBlockedDays(blockedTimeslot, unixStartDate, unixEndDate, timezone),
        ]
      } else {
        unavailableSlotDays = [
          ...unavailableSlotDays,
          ...filterUnavailableSlots(blockedTimeslot, unixStartDate, unixEndDate, timezone),
        ]
      }
    })
  }

  return [blockedCalendarTimeslots, blockedCalendarDays, unavailableSlotDays]
}

export const getFormattedDate = (date, timezone) => {
  return moment(date)
    .tz(timezone || 'America/Chicago')
    .format('hh:mm A')
}

export const getTimeString = (duration) => {
  const hours = duration / ONE_HOUR_DURATION
  const minutes = duration % ONE_HOUR_DURATION
  return `${hours >= ONE_HOUR ? `${Math.floor(hours)}h ` : ''}${minutes ? `${minutes}m` : ''}`
}

export const getDates = (event, timezone) =>
  `${moment(event.startDate)
    .tz(timezone || 'America/Chicago')
    .format('MMMM DD')} - ${moment(event.endDate)
    .tz(timezone || 'America/Chicago')
    .format('MMMM DD')}`

export const getMultidayTime = (event, timezone) => {
  const { startTime, endTime } = event

  // Get today's date string (YYYY-MM-DD)
  const today = moment().tz(timezone).format('YYYY-MM-DD')

  // Create start and end moment objects at midnight in the target timezone
  const start = moment.tz(`${today} 00:00`, 'YYYY-MM-DD HH:mm', timezone).add(startTime, 'minutes')
  const end = moment.tz(`${today} 00:00`, 'YYYY-MM-DD HH:mm', timezone).add(endTime, 'minutes')

  return [start.format('hh:mm A'), end.format('hh:mm A')]
}

export const cutTooLong = (message, length) => {
  if (message && message.length > length) return `${message.slice(0, length)}...`
  return message
}

export const weekdays = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
]

export const getChatFeedFlagsDueToRole = (userRole) => {
  if (['Staff', 'PropertyManager', 'ShiftManager'].includes(userRole)) {
    return ['chatAvailable']
  } else if (['Administrator', 'Manager'].includes(userRole)) {
    return []
  }
}

export const convertToBuildingTimestamp = (value, building, hours = 0, minutes = 0) => {
  const dateObj = new Date(value)
  const year = moment(dateObj).format('YYYY')
  const month = moment(dateObj).format('MM')
  const day = moment(dateObj).format('DD')

  const momentObj = moment(`${year}-${month}-${day}T09:00:00.000Z`)
    .tz(building ? (building[0] ? building[0].timezone : building.timezone) : 'America/Chicago')
    .set('hour', hours)
    .set('minute', minutes)
    .format()

  return new Date(momentObj).getTime()
}

export const convertToUTCTimestamp = (value, hours, minutes) =>
  convertToBuildingTimestamp(value, [{ timezone: 'UTC' }], hours, minutes)

export const dayToTimestamp = (day) => {
  const dayMap = { Su: 0, Mo: 1, Tu: 2, We: 3, Th: 4, Fr: 5, Sa: 6 }
  const dayIndex = dayMap[day]

  if (dayIndex === undefined) {
    return null
  }

  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const baseDate = fromZonedTime('2000-01-02', browserTimezone).setHours(0, 0, 0, 0)
  const targetDate = addDays(baseDate, dayIndex)

  return targetDate.getTime()
}

export const timestampToDay = (identifier) => {
  const dayMap = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

  if (typeof identifier === 'number' && identifier > 6) {
    const date = new Date(identifier)
    const dayOfWeek = date.getDay()
    return dayMap[dayOfWeek]
  }

  return null
}

export const generateRange = (value) => {
  const dateNow = startOfDay(Date.now())
  switch (value) {
    case 'Today':
      return [dateNow, dateNow]
    case 'Yesterday':
      return [subDays(dateNow, 1), dateNow]
    case 'Last 7 days':
      return [subDays(dateNow, 7), dateNow]
    case 'Last 14 days':
      return [subDays(dateNow, 14), dateNow]
    case 'Last 30 days':
      return [subDays(dateNow, 30), dateNow]
    case 'Last 60 days':
      return [subDays(dateNow, 60), dateNow]
    case 'Last 90 days':
      return [subDays(dateNow, 90), dateNow]
    case 'Last 120 days':
      return [subDays(dateNow, 120), dateNow]
    case 'Last year':
      return [subYears(dateNow, 1), dateNow]
    case 'Last two years':
      return [subYears(dateNow, 2), dateNow]
    case 'Last three years':
      return [subYears(dateNow, 3), dateNow]
    case 'Last five years':
      return [subYears(dateNow, 5), dateNow]
    case 'Next 7 days':
      return [dateNow, addDays(dateNow, 7)]
    case 'Next 30 days':
      return [dateNow, addDays(dateNow, 30)]
    default:
      return [null, null]
  }
}

export const timeIntervals = [
  'Today',
  'Yesterday',
  'Last 7 days',
  'Last 14 days',
  'Last 30 days',
  'Last 60 days',
  'Last 90 days',
  'Last 120 days',
  'Last year',
  'Last two years',
  'Last three years',
  'Last five years',
]

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
})

export const formatMoney = (amount) => currencyFormatter.format(amount)

const MARKETING = {
  CommunicationLink: ['/upselling/communicate', '/dashboard/announcements'],
  GroupsLink: ['/upselling/groups', '/groups/dashboard', '/groups/settings'],
  GiftCardLink: ['/upselling/rewards', '/rewards'],
  DigitalSignageLink: ['/upselling/digitalSignage', '/digital-signage'],
  ReputationManagementLink: ['/upselling/reputationManagement', '/social-links'],
  SocialLink: ['/upselling/reputationManagement', '/social-links'],
  WebsiteWidgetsLink: ['/website-widgets'],
}

const OPERATIONS = {
  Instructions: ['/instructions'],
  ChecklistsLink: ['/upselling/checklists', '/checklists'],
  Users: ['/management-users'],
  AmenityReservationsLink: ['/upselling/amenityReservations', '/reservations'],
  MaintenanceRequestLink: ['/upselling/maintenance', '/maintenance/requests', '/maintenance/staff'],
  VisitorManagementLink: ['/upselling/visitorManagement', '/guests'],
  PackageManagementLink: ['/upselling/packageManagement', '/packages'],
  ShiftActionLog: ['/action-log'],
  BookService: ['/dashboard/booking'],
  BuildingEssentialsLink: ['/upselling/buildingEssentials', '/building-essentials'],
  Payments: ['/dashboard/payments'],
  NeighborhoodPerks: ['/neighborhood-perks'],
  Parking: ['/upselling/parking', '/parking'],
  WorkOrdersOffice: ['/work-orders-office'],
}

const DASH_MODAL = {
  HomeScreen: ['/building-dashboard/home-screen'],
  Staff: ['/organisation/staff'],
  Managers: ['/managers'],
  PropertyManagers: ['/property-managers'],
  Services: ['/services'],
  BookingTypes: ['/organisation/bookingtypes'],
  Places: ['/places'],
  ReputationManagement: ['/reputation-management'],
}

const RESERVATIONS_MODAL = {
  Amenities: ['/client-reservations/amenities'],
  MyBookings: ['/client-reservations/my-bookings'],
}

const USER_MENU_MODAL = {
  BookingHistory: ['/user-panel/classes'],
  Profile: ['/user-panel/profile'],
  EmailNotifications: ['/user-panel/notifications'],
  ChangePassword: ['/user-panel/password'],
}

const EXPLORE = {
  Integrations: ['/integrations'],
  ResidentEvents: ['/resident-events', '/upselling/residentEvents'],
}

export const marketingChildrenPathname = Object.values(MARKETING).flat()
export const operationsChildrenPathname = Object.values(OPERATIONS).flat()

export const dashModalChildrenPathname = Object.values(DASH_MODAL).flat()

export const reservationsModalChildrenPathname = Object.values(RESERVATIONS_MODAL).flat()

export const userMenuModalChildrenPathname = Object.values(USER_MENU_MODAL).flat()

export const exploreChildrenPathname = Object.values(EXPLORE).flat()

export const getParentItemHighlighted = (currentPathname, childrenPathname) => {
  return Boolean(childrenPathname.find((pathname) => currentPathname.includes(pathname)))
}

export const formatNumberOfDaysText = (numberOfDays, t) => {
  if (!numberOfDays && Number(numberOfDays) !== 0) return ''
  return t('common:units.days', { count: Number(numberOfDays) })
}

export const isMatchExtension = (file, extensions) => {
  if (!file || !extensions.length) return false
  const extension = file.split('.').pop()
  if (!extension) return false
  return extensions
    .map((ext) => ext.replace(/\./g, '').toLowerCase())
    .includes(extension.toLowerCase())
}

export const deepFilter = (data, keysToRemove = []) => {
  if (typeof data !== 'object' || data === null) {
    return data
  }

  if (Array.isArray(data)) {
    return data.map((item) => deepFilter(item, keysToRemove))
  }

  return Object.keys(data).reduce((filtered, key) => {
    if (!keysToRemove.includes(key)) {
      filtered[key] = deepFilter(data[key], keysToRemove)
    }
    return filtered
  }, {})
}

export const formatTimezoneDate = (
  date = new Date(),
  timezone = 'America/Chicago',
  formatText = 'MM/dd/yyyy',
) =>
  format(toZonedTime(date, timezone), formatText, {
    timeZone: timezone,
  })

export const hasNumbersOnly = (str) => /^\d+$/.test(str)

export const getAuthCookieDomain = () => {
  return window.location.hostname.includes('elevatedliving.com')
    ? '.elevatedliving.com'
    : window.location.hostname.includes('redvike.rocks')
      ? '.redvike.rocks'
      : null
}

export const setAuthCookie = (name, value) => {
  const domain = getAuthCookieDomain()
  Cookies.set(name, value, domain ? { domain } : undefined)
}

export const getCookie = (name) => {
  return Cookies.get(name)
}

export const removeAuthCookie = (name) => {
  const domain = getAuthCookieDomain()
  Cookies.remove(name, domain ? { domain } : undefined)
}

export const isMobileDevice = () => {
  const { userAgent } = window.navigator
  const regex =
    /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i

  return regex.test(userAgent)
}

export const getAuthApiUrl = () => {
  return window.location.hostname.includes('elevatedliving.com')
    ? `${window.location.origin}/api`
    : import.meta.env.VITE_API_URL
}

export const getImageBlob = async (base64Data) => {
  try {
    const response = await fetch(base64Data)
    const blob = await response.blob()
    return blob
  } catch (error) {
    console.error('Error converting base64 to blob:', error)
    throw new Error('Failed to process image')
  }
}

export const getFromStorage = (key, defaultValue) => {
  const value = localStorage.getItem(key)
  if (!value) return defaultValue

  try {
    return JSON.parse(value)
  } catch {
    return defaultValue
  }
}

export const setStorageValue = (key, value) => {
  localStorage.setItem(key, JSON.stringify(value))
}

export const isFile = (value) => value instanceof File || value instanceof Blob

export const isDate = (value) => value instanceof Date
export const saltoSpaceDropdownStyles = () => ({
  control: () => ({
    display: 'flex',
    minHeight: '5vh',
    backgroundColor: APP_COLORS.grey200,
    justifyContent: 'space-between',
    border: `1px solid ${APP_COLORS.grey800}`,
    borderRadius: '5px',
    alignItems: 'center',
    cursor: 'pointer',
    overflow: 'hidden',
  }),
  option: (_, state) => ({
    backgroundColor: state.isSelected ? APP_COLORS.grey300 : APP_COLORS.baseWhite,
    flexDirection: 'row',
    display: 'flex',
    height: '5vh',
    justifyContent: 'flex-start',
    alignItems: 'center',
    paddingLeft: '1vw',
    fontSize: '14px',
    cursor: 'pointer',
    borderBottom: `1px solid ${APP_COLORS.grey300}`,
  }),
  valueContainer: (styles) => ({
    ...styles,
    maxHeight: '10vh',
    overflowY: 'auto',
  }),
})

export const calculateAge = (dateOfBirthTimestamp) => {
  if (!dateOfBirthTimestamp) return null

  const birthDate = new Date(dateOfBirthTimestamp)
  const today = new Date()

  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--
  }

  return age
}

export const canCancelReservation = (role, status) =>
  ['Administrator', 'PropertyManager', 'ShiftManager'].includes(role) &&
  ['approved', 'ongoing', 'completed'].includes(status)

export const canIssueRefund = (role, reservationDate, timezone) => {
  if (role === 'Administrator') return true
  if (reservationDate == null || !timezone) return true
  return !isBefore(
    startOfMonth(toZonedTime(reservationDate, timezone)),
    startOfMonth(toZonedTime(Date.now(), timezone)),
  )
}
